import { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFile,
  faExternalLinkAlt,
  faFolder,
} from '@fortawesome/free-solid-svg-icons';

import './Files.css';
import WebViewer from '@pdftron/webviewer';
import MatterService from '@frontend/AppSettings/services/MatterService';
import MicrosoftHttpService from '../../../AppSettings/services/MicrosoftHttpService';
import {
  getSharePointConfig,
} from '../../../AppSettings/environmentConfig';

const FileList = ({ matterId }) => {
  const [webViewerInstance, setWebViewerInstance] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const { siteId, driveId } = getSharePointConfig();
  const [folderHistory, setFolderHistory] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState(''); // For new folder creation
  const [newFileName, setNewFileName] = useState(''); // For new file creation
  const [newFileType, setNewFileType] = useState('docx'); // For selecting file type
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // "folder", "file", or "upload"

  const [viewerContainer, setViewerContainer] = useState(null);

  useEffect(() => {
    if (!webViewerInstance && viewerContainer) {
      const initializeWebViewer = async () => {
        const instance = await WebViewer(
          {
            path: '../../../lib',
            licenseKey:
              'demo:1709879001877:7f2184640300000000567a2901e2a43ee3f7c5441b9cdb91949d35b6fa',
          },
          viewerContainer
        );

        setWebViewerInstance(instance);
      };

      initializeWebViewer();
    }
  }, [webViewerInstance, viewerContainer]);

  const fetchMatterDetails = async () => {
    try {
      const data = await MatterService.getMatterData(matterId);
      return data;
    } catch (error) {
      console.log('Error fetching matter details: ' + error.message);
      return null;
    }
  };

  const loadWebViewerDocument = async (documentURL, extension) => {
    if (!webViewerInstance) {
      console.error('WebViewer instance not available.');
      return;
    }

    const { documentViewer } = webViewerInstance.Core;
    webViewerInstance.UI.loadDocument(documentURL, {
      extension: extension,
    });
    documentViewer.addEventListener('documentLoaded', async () => {
      const doc = documentViewer.getDocument();
      const keys = await doc.getTemplateKeys();
      if (keys.length !== 0) {
        const matterDetails = await fetchMatterDetails();
        if (matterDetails) {
          await doc.applyTemplateValues(matterDetails);
          const data = await doc.getFileData({
            downloadType: 'templateFilledOffice',
            officeOptions: {
              doTemplatePrep: true,
            },
          });
          const arr = new Uint8Array(data);
          const blob = new Blob([arr], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
          const fileName = 'template-filled.docx';

          try {
            const microsoftService = new MicrosoftHttpService([
              'https://graph.microsoft.com/Files.ReadWrite.All',
            ]);
            await microsoftService.uploadSharePointFile(
              siteId,
              driveId,
              currentFolder.id,
              fileName,
              blob
            );
            console.log('File uploaded successfully');
          } catch (error) {
            console.error('Error uploading file:', error);
          }
        }
      }
    });
  };

  const createSubFolder = useCallback(
    async (folderName, parentFolderId) => {
      if (!parentFolderId) {
        console.error('Parent folder ID is required to create a subfolder.');
        return null;
      }

      try {
        const microsoftService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.ReadWrite.All',
        ]);
        const folder = await microsoftService.createSharePointFolder(
          siteId,
          driveId,
          parentFolderId,
          folderName
        );
        return folder;
      } catch (error) {
        console.error('Error creating subfolder:', error);
        return null;
      }
    },
    [siteId, driveId]
  );

  const handleFileClick = (file) => {
    if (!webViewerInstance) {
      console.error('WebViewer instance not initialized.');
      return;
    }

    loadWebViewerDocument(
      file['@microsoft.graph.downloadUrl'],
      file.name.split('.').pop()
    );
  };

  const handleOpenInNewTab = (file, event) => {
    event.stopPropagation(); // Prevent the event from bubbling up to the row click handler
    window.open(file.webUrl, '_blank');
  };

  const fetchFolderContent = useCallback(
    async (folderId) => {
      try {
        const microsoftService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.Read.All',
        ]);
        const items = await microsoftService.getSharePointFolderChildren(
          siteId,
          driveId,
          folderId
        );
        return items;
      } catch (error) {
        console.error('Error fetching folder content:', error);
        return [];
      }
    },
    [siteId, driveId]
  );

  const handleFolderClick = async (folder) => {
    setFolderHistory((prevHistory) => [...prevHistory, currentFolder]);
    setCurrentFolder(folder);
  };

  const handleBackButtonClick = async () => {
    if (folderHistory.length > 0) {
      const previousFolder = folderHistory.pop();
      setCurrentFolder(previousFolder);
    }
  };

  const handleCreateSubFolder = async () => {
    if (newFolderName.trim() !== '' && currentFolder) {
      const newSubFolder = await createSubFolder(
        newFolderName,
        currentFolder.id
      );
      if (newSubFolder) {
        console.log('Subfolder created successfully:', newSubFolder.name);
        setFolders((prevFolders) => [...prevFolders, newSubFolder]);
        fetchFolderContent(currentFolder); // Refresh the folder list
        setNewFolderName(''); // Clear the input field after successful creation
      }
    } else {
      console.error('No current folder selected or folder name is empty.');
    }
  };

  const handleFileUpload = useCallback(
    async (file, folderId) => {
      try {
        const microsoftService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.ReadWrite.All',
        ]);
        const uploadedFile = await microsoftService.uploadSharePointFile(
          siteId,
          driveId,
          folderId,
          file
        );
        return uploadedFile;
      } catch (error) {
        console.error('Error uploading file:', error);
        return null;
      }
    },
    [siteId, driveId]
  );
  const handleCreateFile = useCallback(async () => {
    if (newFileName.trim() !== '') {
      let fileContent;
      let contentType;
      let fileExtension;

      switch (newFileType) {
        case 'docx':
          fileContent = new Blob([''], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
          contentType =
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          fileExtension = '.docx';
          break;
        case 'xlsx':
          fileContent = new Blob([''], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          contentType =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          fileExtension = '.xlsx';
          break;
        case 'pptx':
          fileContent = new Blob([''], {
            type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          });
          contentType =
            'application/vnd.openxmlformats-officedocument.presentationml.presentation';
          fileExtension = '.pptx';
          break;
        case 'txt':
          fileContent = new Blob([''], { type: 'text/plain' });
          contentType = 'text/plain';
          fileExtension = '.txt';
          break;
        default:
          console.error('Unsupported file type');
          return;
      }

      try {
        const microsoftService = new MicrosoftHttpService([
          'https://graph.microsoft.com/Files.ReadWrite.All',
        ]);
        const file = new File([fileContent], `${newFileName}${fileExtension}`, {
          type: contentType,
        });
        const uploadedFile = await microsoftService.uploadSharePointFile(
          siteId,
          driveId,
          currentFolder.id,
          file
        );

        if (uploadedFile) {
          console.log('File created successfully');
          fetchFolderContent(currentFolder); // Refresh the folder content
          setNewFileName(''); // Clear the input field
        } else {
          console.error('Error creating file');
        }
      } catch (error) {
        console.error('Error creating file:', error);
      }
    }
  }, [
    newFileName,
    newFileType,
    siteId,
    driveId,
    currentFolder,
    fetchFolderContent,
  ]);

  const handleShowModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewFolderName('');
    setNewFileName('');
  };

  const handleModalSubmit = () => {
    if (modalType === 'folder') {
      handleCreateSubFolder();
    } else if (modalType === 'file') {
      handleCreateFile();
    }
    handleCloseModal();
  };

  return (
    <div>
      <div className="MD-Tab-Container" style={{ display: 'flex' }}>
        <div className="MD-File-files-sidebar">
          <div className="MD-File-topbar-Create-Dropdown-Container">
            <div className="MD-File-file-nav-buttons">
              {folderHistory.length > 0 && (
                <button
                  className="MD-File-create-button"
                  onClick={handleBackButtonClick}
                >
                  <FontAwesomeIcon
                    icon={faArrowLeft}
                    className="MD-icon-circle"
                  />
                </button>
              )}
              <div className="MD-File-dropdown">
                <button className="MD-File-create-button">+</button>
                <div className="MD-File-dropdown-content">
                  <button onClick={() => handleShowModal('folder')}>
                    Create Folder
                  </button>
                  <button onClick={() => handleShowModal('file')}>
                    Create File
                  </button>
                  <button
                    onClick={() =>
                      document.getElementById('file-upload').click()
                    }
                  >
                    Upload File
                  </button>
                  <input
                    type="file"
                    id="file-upload"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="MD-File-files-Subdirectory-section">
            <ul>
              {folders.map((folder) => (
                <div key={folder.id}>
                  <li
                    onClick={() => handleFolderClick(folder)}
                    className="MD-File-folder-item"
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={faFolder} /> {folder.name}
                  </li>
                </div>
              ))}
            </ul>
            <ul>
              {files.map((file) => (
                <div key={file.id}>
                  <li>
                    <button
                      className="MD-File-file-button"
                      onClick={() => handleFileClick(file)}
                    >
                      <div className="MD-File-file-info">
                        <FontAwesomeIcon icon={faFile} /> {file.name}
                      </div>
                      <div className="MD-File-file-actions">
                        <FontAwesomeIcon
                          icon={faExternalLinkAlt}
                          onClick={(e) => handleOpenInNewTab(file, e)}
                          style={{ cursor: 'pointer', marginLeft: '10px' }}
                        />
                      </div>
                    </button>
                  </li>
                </div>
              ))}
            </ul>
          </div>
        </div>

        <div className="MD-File-file-preview-area">
          <div
            className="MD-MyComponent"
            ref={(el) => {
              if (el && !viewerContainer) setViewerContainer(el);
            }}
            style={{ height: '100vh' }}
          ></div>
        </div>

        {showModal && (
          <div
            id="myModal"
            className="MD-File-modal"
            style={{ display: 'block' }}
          >
            <div className="MD-File-modal-content">
              <span className="MD-File-close" onClick={handleCloseModal}>
                &times;
              </span>
              <h2>
                {modalType === 'folder'
                  ? 'Create New Folder'
                  : 'Create New File'}
              </h2>
              {modalType === 'folder' && (
                <div>
                  <label>Folder Name</label>
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                </div>
              )}
              {modalType === 'file' && (
                <div>
                  <label>File Name</label>
                  <input
                    type="text"
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                  />
                  <label>File Type</label>
                  <select
                    value={newFileType}
                    onChange={(e) => setNewFileType(e.target.value)}
                  >
                    <option value="docx">Word Document (.docx)</option>
                    <option value="xlsx">Excel Spreadsheet (.xlsx)</option>
                    <option value="pptx">
                      PowerPoint Presentation (.pptx)
                    </option>
                    <option value="txt">Text File (.txt)</option>
                  </select>
                </div>
              )}
              <button onClick={handleModalSubmit}>Create</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileList;
